import { TitleCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

import { AdminBackend } from '@libs/src/admin/admin.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { DialogConfirmPromptService } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.service';
import { ToastService } from '@libs/src/toast/toast.service';

import {
  getCurrentPageDisplayData,
  pageSize,
} from './action-item-drawer.helpers';
import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import {
  AccountGroupMembership,
  AccountSegmentSelection,
} from '@libs/src/models/account.model';
import {
  ACTION_ITEM_DELETE_ACCOUNT,
  ACTION_ITEM_REMOVE_ACCOUNT_FROM_GROUP,
  ACTION_ITEM_STATUS_DISMISSED,
  ACTION_ITEM_STATUS_PENDING,
  ACTION_ITEM_STATUSES,
  ActionItem,
  DeleteActionItem,
  ExtendedPayloadActionItem,
} from '@libs/src/models/action-item.model';
import {
  ACTION_QUEUE_DEACTIVATE,
  ACTION_QUEUE_DELETE,
  ACTION_QUEUE_INVITE,
  ACTION_QUEUE_UPDATE,
  ActionQueue,
} from '@libs/src/models/action-queue.model';
import {
  Group,
  GroupCustomSegment,
  GroupHiddenCustomSegment,
} from '@libs/src/models/group.model';
import {
  MappingOption,
  role,
} from '@main-client/src/app/attribute-mapping/attribute-mapping.helper';
import { PreregistrationAccount } from '@main-client/src/app/invite-csv/invite-csv-view/invite-csv-view.interface';
import { flattenObjectKeys } from '@main-client/src/app/profile-form-dialogs/profile-form.helper';

import chunk from 'lodash-es/chunk';
import pick from 'lodash-es/pick';

const actionTabOptions = {
  deactivate: $localize`:@@actionItemDrawer.actionTab.deactivate:To ${ACTION_QUEUE_DEACTIVATE} `,
  delete: $localize`:@@actionItemDrawer.actionTab.delete:To ${ACTION_QUEUE_DELETE}`,
  invite: $localize`:@@actionItemDrawer.actionTab.invite:To ${ACTION_QUEUE_INVITE}`,
  update: $localize`:@@actionItemDrawer.actionTab.update:To ${ACTION_QUEUE_UPDATE}`,
};
const descriptionOptions = {
  deactivate: $localize`:@@actionItemDrawer.actionTab.description.deactivate:The following members' accounts can be <b>deactivated</b>`,
  delete: $localize`:@@actionItemDrawer.actionTab.description.delete:The following members' accounts can be <b>deleted</b>`,
  invite: $localize`:@@actionItemDrawer.actionTab.description.invite:The following members' accounts can be <b>invited</b>`,
  update: $localize`:@@actionItemDrawer.actionTab.description.update:The following members' accounts can be <b>updated</b>`,
};
const noMembersDescription = {
  deactivate: $localize`:@@actionItemDrawer.completedTab.description.deactivated:No members deactivated yet`,
  delete: $localize`:@@actionItemDrawer.completedTab.description.deleted:No members deleted yet`,
  invite: $localize`:@@actionItemDrawer.completedTab.description.invited:No members invited yet`,
  update: $localize`:@@actionItemDrawer.completedTab.description.updated:No members updated yet`,
};
const subtitleOptions = {
  deactivate: $localize`:@@actionItemDrawer.actionTab.subtitle.deactivate:Skip deactivating specific people by deselecting them from the list below`,
  delete: $localize`:@@actionItemDrawer.actionTab.subtitle.delete:Skip deleting specific people by deselecting them from the list below`,
  invite: $localize`:@@actionItemDrawer.actionTab.subtitle.invite:Skip inviting specific people by deselecting them from the list below`,
  update: $localize`:@@actionItemDrawer.actionTab.subtitle.update:Skip updating specific people by deselecting them from the list below`,
};

@Component({
  providers: [DestroySubscriptionsService, TitleCasePipe],
  selector: 'app-action-item-drawer',
  templateUrl: './action-item-drawer.component.html',
})
export class ActionItemDrawerComponent implements OnInit {
  @Input() actionItems: ActionItem[];
  @Input() actionQueueType: ActionQueue['action'];
  @Input() actionQueueStatus: ActionQueue['status'];
  @Input() group: Group;
  isLoading = false;
  actionQueueId: ActionQueue['_id'];
  actionTabTitle: string;
  completedTabTitle: string;
  deleteOptions: { [key: string]: string };
  description: string;
  displayActionData: ActionItem[] = [];
  errorItemData: ActionItem[] = [];
  memberCount: number;
  noMembers: string;
  profileFieldHeaders: string[] = [];
  selectOptions: MappingOption[];
  successItems: ActionItem[] = [];
  selectionItems: MappingOption[][] = [];
  subtitle: string;
  successPopUp: string;
  errorItemCount = 0;
  completedItemsCount = 0;
  pageIndex = 1;
  pageSize = pageSize;
  dialogValues: {
    [key: string]: {
      action: string;
      color: 'accent' | 'primary' | 'warn';
      title: string;
    };
  } = {
    deactivate: {
      action: 'Confirm',
      color: 'warn',
      title: $localize`:@@actionItemDrawer.confirmationPrompt.deactivate:Deactivate accounts?`,
    },
    delete: {
      action: 'Confirm',
      color: 'warn',
      title: $localize`:@@actionItemDrawer.confirmationPrompt.delete:Delete accounts?`,
    },
    invite: {
      action: 'Send invites',
      color: 'primary',
      title: $localize`:@@actionItemDrawer.confirmationPrompt.invite:Send invites to new users for this Hub?`,
    },
    update: {
      action: 'Update',
      color: 'primary',
      title: $localize`:@@actionItemDrawer.confirmationPrompt.update:Update accounts for this Hub?`,
    },
  };
  actionTabOptions: { [key: string]: string } = actionTabOptions;
  descriptionOptions: { [key: string]: string } = descriptionOptions;
  completedTabTitleOptions: { [key: string]: string } = {
    deactivate: $localize`:@@actionItemDrawer.completedTabTitle.deactivated:Deactivated`,
    delete: $localize`:@@actionItemDrawer.completedTabTitle.deleted:Deleted`,
    invite: $localize`:@@actionItemDrawer.completedTabTitle.invited:Invited`,
    update: $localize`:@@actionItemDrawer.completedTabTitle.updated:Updated`,
  };
  noMembersDescription: { [key: string]: string } = noMembersDescription;
  subtitleOptions: { [key: string]: string } = subtitleOptions;
  actionQueueToDeactivate = ACTION_QUEUE_DEACTIVATE;
  actionQueueToDelete = ACTION_QUEUE_DELETE;
  actionQueueToInvite = ACTION_QUEUE_INVITE;
  actionQueueToUpdate = ACTION_QUEUE_UPDATE;
  groupCustomSegments: Pick<GroupCustomSegment, '_id' | 'title'>[];
  groupHiddenCustomSegments: Pick<GroupHiddenCustomSegment, '_id' | 'title'>[];
  profileValues: { [key: string]: string[] };
  successValues: { [key: string]: string[] };
  errorValues: { [key: string]: string[] };
  columnWidth: string;
  useFullName: boolean;

  constructor(
    private readonly adminBackend: AdminBackend,
    public confirmDialogPrompt: DialogConfirmPromptService,
    private readonly drawerRef: NzDrawerRef<ActionItemDrawerComponent>,
    public readonly route: ActivatedRoute,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit() {
    this.actionQueueId = this.actionItems[0].action_queue;
    this.actionTabTitle = this.actionTabOptions[this.actionQueueType];
    this.completedTabTitle =
      this.completedTabTitleOptions[this.actionQueueType];
    this.deleteOptions = {
      [ACTION_ITEM_DELETE_ACCOUNT]: $localize`:@@actionItemDrawer.selectOptions.deleteAccount:Delete account permanently`,
      [ACTION_ITEM_REMOVE_ACCOUNT_FROM_GROUP]: $localize`:@@actionItemDrawer.selectOptions.removeFromHub:Remove from hub ${this.group.name}`,
    };
    this.selectOptions = [
      {
        label: this.deleteOptions['delete-account'],
        value: ACTION_ITEM_DELETE_ACCOUNT,
      },
      {
        label: this.deleteOptions['remove-account-from-group'],
        value: ACTION_ITEM_REMOVE_ACCOUNT_FROM_GROUP,
      },
    ];
    this.errorItemData = this.actionItems.filter(
      ({ status }) => ACTION_ITEM_STATUSES.ACTION_ITEM_STATUS_ERROR === status,
    );
    this.errorItemCount = this.errorItemData.length;
    this.successItems = this.actionItems.filter(
      ({ status }) =>
        ACTION_ITEM_STATUSES.ACTION_ITEM_STATUS_COMPLETED === status,
    );
    this.completedItemsCount = this.successItems.length;
    this.displayActionData = this.actionItems.reduce(
      (acc: ActionItem[], currentActionItem, index) => {
        if (currentActionItem.status !== ACTION_ITEM_STATUS_PENDING) {
          return acc;
        }
        if (
          currentActionItem.action === ACTION_ITEM_DELETE_ACCOUNT ||
          currentActionItem.action === ACTION_ITEM_REMOVE_ACCOUNT_FROM_GROUP
        ) {
          if (!currentActionItem.account?._id) {
            this.actionItems[index].skip = true;
            return acc;
          }
          const is10KCAccount = this.is10KCAccount(
            currentActionItem.account.email,
          );
          this.actionItems[index].skip = !!is10KCAccount;
        }
        if (
          ACTION_ITEM_STATUSES.ACTION_ITEM_STATUS_DISMISSED ===
          currentActionItem.status
        ) {
          this.actionItems[index].skip = true;
        }
        return [...acc, currentActionItem];
      },
      [],
    );
    this.displayActionData.sort((a, b) => Number(a.skip) - Number(b.skip));
    const displayActionDataChunks = chunk(
      this.displayActionData,
      this.pageSize,
    );
    this.selectionItems = displayActionDataChunks.map((actionItems) =>
      actionItems.map((data) => ({
        label: this.deleteOptions[data.action],
        value: data.action,
      })),
    );
    this.memberCount = this.displayActionData.filter(
      (item) => !item.skip,
    ).length;
    this.description = this.descriptionOptions[this.actionQueueType];
    this.noMembers = this.noMembersDescription[this.actionQueueType];
    this.subtitle = this.subtitleOptions[this.actionQueueType];
    this.successPopUp = this.setSuccessPopUp();
    this.useFullName = true;
    this.groupCustomSegments = (this.group.custom_segments || []).map(
      (segment) => ({ _id: segment._id, title: segment.title }),
    );
    this.groupHiddenCustomSegments = (
      this.group.hidden_custom_segments || []
    ).map((segment) => ({ _id: segment._id, title: segment.title }));
    if (
      ![ACTION_QUEUE_DELETE, ACTION_QUEUE_DEACTIVATE].includes(
        this.actionQueueType,
      )
    ) {
      this.profileFieldHeaders = this.setProfileFieldHeaders(
        this.actionItems as ExtendedPayloadActionItem[],
      );
      this.columnWidth = `${(this.profileFieldHeaders.length * 45).toString()}%`;
      this.profileValues = this.displayActionData.reduce(
        (acc, actionItem: ExtendedPayloadActionItem) => ({
          ...acc,
          [actionItem._id]: this.getProfileValues(
            actionItem.payload_translated,
          ),
        }),
        {},
      );
      this.successValues = this.successItems.reduce(
        (acc, actionItem: ExtendedPayloadActionItem) => ({
          ...acc,
          [actionItem._id]: this.getProfileValues(
            actionItem.payload_translated,
          ),
        }),
        {},
      );
      this.errorValues = this.errorItemData.reduce(
        (acc, actionItem: ExtendedPayloadActionItem) => ({
          ...acc,
          [actionItem._id]: this.getProfileValues(
            actionItem.payload_translated,
          ),
        }),
        {},
      );
    }
  }

  getNestedHeaders(item: ExtendedPayloadActionItem) {
    return [
      ...Object.keys(item.payload_translated.education?.[0] || []).reduce(
        (acc: string[], key: string) => {
          if ('_id' === key) {
            return acc;
          }
          return [...acc, `education.${key}`];
        },
        [],
      ),
      ...Object.keys(item.payload_translated?.experience?.[0] || []).reduce(
        (acc: string[], key: string) => {
          if ('_id' === key) {
            return acc;
          }
          return [...acc, `experience.${key}`];
        },
        [],
      ),
    ];
  }

  setProfileFieldHeaders(actionItems: ActionItem[]) {
    if (
      [this.actionQueueToDeactivate, this.actionQueueToDelete].includes(
        this.actionQueueType,
      )
    ) {
      return [];
    }
    let headers: string[] = [];
    actionItems.forEach((item: ExtendedPayloadActionItem) => {
      const { first_name, last_name } = item.payload_translated;
      this.useFullName = this.useFullName && !(first_name || last_name);
      const nestedHeaders = this.getNestedHeaders(item);
      const basicHeaders = [
        ...Object.keys(
          pick(item.payload_translated, [
            'about',
            'external_id',
            'geo_location',
            'isAdmin',
            'portrait_original_url',
            'status',
            'website',
          ]),
        ),
        'locale',
      ];
      if (
        !item.payload_translated.groups ||
        !item.payload_translated.groups.length
      ) {
        return [...basicHeaders, ...nestedHeaders];
      }
      const accountGroupInformation = item.payload_translated
        .groups[0] as AccountGroupMembership;
      const hiddenSegmentSelectionHeaders = (
        accountGroupInformation?.hidden_segment_selections || []
      ).map(
        (selection: AccountSegmentSelection) =>
          this.groupHiddenCustomSegments.find(
            (groupSegment) => selection.custom_segment === groupSegment._id,
          )?.title,
      );
      const customSegmentSelectionHeaders = (
        accountGroupInformation?.segment_selections || []
      ).map(
        (selection: AccountSegmentSelection) =>
          this.groupCustomSegments.find(
            (groupSegment) => selection.custom_segment === groupSegment._id,
          )?.title,
      );
      const groupHeaders = [
        'role',
        ...customSegmentSelectionHeaders,
        ...hiddenSegmentSelectionHeaders,
      ];
      headers = [
        ...headers,
        ...groupHeaders,
        ...nestedHeaders,
        ...basicHeaders,
      ];
    });
    return [...new Set(headers)];
  }

  getProfileValues(data: ExtendedPayloadActionItem['payload_translated']) {
    return this.profileFieldHeaders?.map((header) => {
      if ('locale' === header) {
        return data.locale_config?.locale_selected || 'en-US';
      }
      const flatHeaderValue =
        data[header as keyof ExtendedPayloadActionItem['payload_translated']];
      if (flatHeaderValue) {
        return flatHeaderValue;
      }
      const value = header.split('.');
      const nestedHeaderValue = (
        data[
          value[0] as keyof ExtendedPayloadActionItem['payload_translated']
        ] as Array<{
          [key: string]: string;
        }>
      )?.[0]?.[value[1] as keyof PreregistrationAccount];
      if (nestedHeaderValue) {
        return nestedHeaderValue;
      }
      const groupSegmentId = [
        ...this.groupCustomSegments,
        ...this.groupHiddenCustomSegments,
      ].find((segment) => header === segment.title)?._id;
      const segmentSelection = [
        ...(data.groups[0]?.hidden_segment_selections || []),
        ...(data.groups[0]?.segment_selections || []),
      ].find((selection) => selection.custom_segment === groupSegmentId);
      if (segmentSelection) {
        return 0 < segmentSelection?.multiselected_nodes?.length
          ? segmentSelection.multiselected_nodes.join(',')
          : segmentSelection.node;
      }
      if (role === header) {
        return data.groups[0]?.role;
      }
      return null;
    });
  }

  setSuccessPopUp() {
    const options: { [key: string]: string } = {
      deactivate: $localize`:@@actionItemDrawer.completedTab.successPopup.deactivate:${this.successItems.length} members have been deactivated in ${this.group.name}`,
      delete: $localize`:@@actionItemDrawer.completedTab.successPopup.delete:${this.successItems.length} members have been deleted in ${this.group.name}`,
      invite: $localize`:@@actionItemDrawer.completedTab.successPopup.invite:${this.successItems.length} members have been invited in ${this.group.name}`,
      update: $localize`:@@actionItemDrawer.completedTab.successPopup.update:${this.successItems.length} members have been updated in ${this.group.name}`,
    };
    return options[this.actionQueueType];
  }
  is10KCAccount(email: string) {
    return ['10kc.com', 'tenthousandcoffees.com'].some((domain) =>
      email.includes(domain),
    );
  }

  compareSelectedValues(a: MappingOption, b: MappingOption) {
    if (!a || !b) {
      return false;
    }
    return a.value === b.value;
  }

  areAllSelected(pageIndex: number) {
    const { allSelectedValuesLength, currentDisplayData } =
      getCurrentPageDisplayData(
        this.displayActionData as ActionItem[],
        pageIndex,
      );
    return allSelectedValuesLength === currentDisplayData.length;
  }

  isIndeterminate(pageIndex: number) {
    const { allSelectedValuesLength, currentDisplayData } =
      getCurrentPageDisplayData(
        this.displayActionData as ActionItem[],
        pageIndex,
      );
    return (
      allSelectedValuesLength < currentDisplayData.length &&
      0 < allSelectedValuesLength
    );
  }

  selectAll(pageIndex: number) {
    const { allSelectedValuesLength, currentDisplayData } =
      getCurrentPageDisplayData(
        this.displayActionData as ActionItem[],
        pageIndex,
      );
    const selectedMembersCount = allSelectedValuesLength;
    currentDisplayData.forEach((data, index) => {
      this.displayActionData[pageIndex * index].skip = false;
      const actionItemIndex = this.actionItems.findIndex(
        (item) => data._id === item._id,
      );
      this.actionItems[actionItemIndex].skip = false;
    });
    this.memberCount =
      this.memberCount - selectedMembersCount + currentDisplayData.length;
  }

  selectAllAction(action: string) {
    let updatedMemberCount = 0;
    this.displayActionData.forEach((data, index) => {
      if (data.skip) {
        return;
      }
      this.displayActionData[index].skip = false;
      this.displayActionData[index].action = action;
      const selectionIndex = index % this.pageSize;
      const pageIndex = Math.floor(index / this.pageSize);
      this.selectionItems[pageIndex][selectionIndex] = {
        label: this.deleteOptions[action],
        value: action,
      };
      const actionItemIndex = this.actionItems.findIndex(
        (item) => data._id === item._id,
      );
      this.actionItems[actionItemIndex].skip = false;
      this.actionItems[actionItemIndex].action = action;
      this.updateActionItemAction(action, data as DeleteActionItem);
      updatedMemberCount += 1;
    });
    this.memberCount = updatedMemberCount;
  }

  setSkipStatus(
    actionItemId: ActionItem['_id'],
    displayIndex: number,
    skippedStatus: boolean,
  ) {
    const index = this.actionItems.findIndex(
      (item) => actionItemId === item._id,
    );
    if (0 > index) {
      return;
    }
    this.actionItems[index].skip = !skippedStatus;
    this.displayActionData[displayIndex].skip = !skippedStatus;
    this.memberCount =
      this.displayActionData.filter((item) => !item.skip)?.length || 0;
  }

  updateAction(
    actionItemId: DeleteActionItem['_id'],
    displayIndex: number,
    selection: MappingOption,
  ) {
    const index = this.actionItems.findIndex(
      (item) => actionItemId === item._id,
    );
    if (0 > index) {
      return;
    }
    this.selectionItems[this.pageIndex - 1][displayIndex] = {
      label: selection.label,
      value: selection.value,
    };
    this.actionItems[index].action = selection.value;
    return this.updateActionItemAction(
      selection.value,
      this.actionItems[index],
    );
  }

  openConfirmationDialog() {
    this.confirmDialogPrompt
      .openDialog({
        cancel: $localize`:@@global.cancel:Cancel`,
        color: this.dialogValues[this.actionQueueType].color,
        confirm: this.dialogValues[this.actionQueueType].action,
        content: this.generateDescriptionString(),
        title: this.dialogValues[this.actionQueueType].title,
      })
      .afterClosed()
      .pipe(
        filter((data: boolean) => data),
        tap(() => {
          this.startActionItemsProcessing();
        }),
      )
      .subscribe();
  }

  generateDescriptionString() {
    switch (this.actionQueueType) {
      case ACTION_QUEUE_DEACTIVATE: {
        return $localize`:@@actionItemDrawer.confirmationPrompt.description.deactivate:You are deactivating ${this.memberCount} members in ${this.group.name} Hub.`;
      }
      case ACTION_QUEUE_DELETE: {
        const removedAndDeletedCounts = this.actionItems.reduce(
          (result, item) => {
            if (item.skip) {
              return result;
            }
            if (ACTION_ITEM_REMOVE_ACCOUNT_FROM_GROUP === item.action) {
              return { ...result, removed: result.removed + 1 };
            }
            return { ...result, deleted: result.deleted + 1 };
          },
          { deleted: 0, removed: 0 },
        );
        if (
          0 < removedAndDeletedCounts.removed &&
          0 < removedAndDeletedCounts.deleted
        ) {
          return $localize`:@@actionItemDrawer.confirmationPrompt.description.combined:You are removing ${removedAndDeletedCounts.removed} accounts from ${this.group.name} Hub and permanently deleting ${removedAndDeletedCounts.deleted} accounts.`;
        }
        return 0 < removedAndDeletedCounts.removed
          ? $localize`:@@actionItemDrawer.confirmationPrompt.description.remove:You are removing ${removedAndDeletedCounts.removed} accounts from ${this.group.name} Hub.`
          : $localize`:@@actionItemDrawer.confirmationPrompt.description.delete:You are deleting ${removedAndDeletedCounts.deleted} accounts.`;
      }
      case ACTION_QUEUE_INVITE: {
        return $localize`:@@actionItemDrawer.confirmationPrompt.description.invite:You are sending invites for ${this.memberCount} members to join ${this.group.name} Hub.`;
      }
      case ACTION_QUEUE_UPDATE: {
        return $localize`:@@actionItemDrawer.confirmationPrompt.description.update:You are updating information for ${this.memberCount} members of ${this.group.name} Hub.`;
      }
      default: {
        return this.actionQueueType;
      }
    }
  }

  onSearchInputChange(searchTerm: string) {
    this.displayActionData = this.actionItems.filter((data) => {
      const flattenedData = flattenObjectKeys(data);
      return Object.values(flattenedData).some((value) => {
        if (Array.isArray(value) || !value) {
          return;
        }
        return value
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase().trim());
      });
    });
  }

  startActionItemsProcessing() {
    this.isLoading = true;
    const dismissedItems = this.actionItems.filter((item) => item.skip);
    if (0 === dismissedItems.length) {
      this.adminBackend.v3
        .processActionQueue(this.actionQueueId)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.toastService.showError(getServerError(error));
            this.isLoading = false;
            return of({ status: 'error' });
          }),
        )
        .subscribe({
          next: (response) => {
            this.closeDrawer(response.status);
            this.isLoading = false;
          },
        });
      return;
    }
    const updateObservables = dismissedItems.map((dismissedItem) =>
      this.adminBackend.v3.patchActionItem(
        dismissedItem._id,
        this.actionQueueId,
        {
          action: dismissedItem.action,
          status: ACTION_ITEM_STATUS_DISMISSED,
        },
      ),
    );
    forkJoin(updateObservables)
      .pipe(
        switchMap(() =>
          this.adminBackend.v3.processActionQueue(this.actionQueueId),
        ),
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError(getServerError(error));
          this.isLoading = false;
          return of({ status: 'error' });
        }),
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.closeDrawer(response.status);
        },
      });
  }

  updateActionItemAction(action: string, item: DeleteActionItem) {
    this.adminBackend.v3
      .patchActionItem(item._id, this.actionQueueId, {
        action,
        status: item.status,
      })
      .subscribe();
  }

  closeDrawer(status: string) {
    this.drawerRef.close({
      status,
      activeItemCount: this.memberCount,
      date: new Date(),
    });
  }
}
